import React, { useEffect, useState } from 'react';
import { Alert, Button, Space, Table } from 'antd';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OrderDetailSectionCard from '../components/OrderDetailSectionCard';
import { fetchStockValuation, type StockValuation } from '../stockValuation/stockValuationApi';
import { formatCurrency } from '../../../utils/currency';

interface Props {
  supplierOrderId: string;
  showFreight: boolean;
  landedCostPLN: number | null;
  revision: unknown;
  children: React.ReactNode;
}

// Reuse the canonical freight allocation read model; do not recalculate truck shares.
export default function SupplierOrderPurchaseCostsSection({ supplierOrderId, showFreight, landedCostPLN, revision, children }: Props) {
  const { t } = useTranslation(['orders', 'common']);
  const key = (name: string) => t(`orders:supplierOrders.finance.${name}`);
  const [data, setData] = useState<StockValuation>();
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    setData(undefined); setFailed(false);
    if (!showFreight) return;
    let active = true;
    fetchStockValuation(supplierOrderId).then(value => { if (active) setData(value); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [supplierOrderId, showFreight, revision, retry]);
  const freightKnown = data && data.transports.every(row => row.amountPLN != null);
  const total = showFreight ? (freightKnown && landedCostPLN != null ? data.freightPLN + landedCostPLN : null) : null;
  return <OrderDetailSectionCard title={key('costsTitle')} forceRender
    extra={<span>{total == null ? key('totalUnknown') : formatCurrency(total, 'PLN')}</span>}>
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {showFreight && (failed ? <Alert type="error" message={key('freightFailed')} action={<Button onClick={() => setRetry(value => value + 1)}>{t('common:refresh')}</Button>} /> :
        <Table rowKey="id" loading={!data} dataSource={data?.transports ?? []} pagination={false} size="small" locale={{ emptyText: key('noFreight') }} columns={[
          { title: key('transport'), render: (_, row) => <Link to={`/app/transports/${row.id}`}>{row.label}</Link> },
          { title: key('allocatedFreight'), align: 'right', render: (_, row) => row.amountPLN == null ? '—' : formatCurrency(row.amountPLN, 'PLN') },
        ]} />)}
      {children}
    </Space>
  </OrderDetailSectionCard>;
}
